// components/ui/custom-dropdown.tsx
import React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface DropdownItem {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  bgColor?: string;
  textColor?: string;
}

interface CustomDropdownProps {
  items?: DropdownItem[];
  onSubmit: (value: string, label: string) => void;
  placeholder?: string;
  // This should be the 'label' of the item
  selectedValue?: string;
  className?: string;
  triggerClassName?: string;
  disabled?: boolean;
  loading?: boolean;
  hiddenItems?: string[];
}

const interestItems: DropdownItem[] = [
  {
    label: 'Partner Interested',
    value: 'partner_interested',
    // icon: Heart,
    bgColor: '#e0f7fa',
    textColor: '#00796b',
  },
  {
    label: 'Partner Not Interested',
    value: 'partner_not_interested',
    // icon: Zap,
    bgColor: '#fbe9e7',
    textColor: '#d84315',
  },
  {
    label: 'Demo Scheduled',
    value: 'demo_scheduled',
    // icon: Star,
    bgColor: '#fffde7',
    textColor: '#fbc02d',
  },
  {
    label: 'Demo Followup',
    value: 'demo_followup',
    // icon: Globe,
    bgColor: '#ede7f6',
    textColor: '#5e35b1',
  },
  {
    label: 'Maxx',
    value: 'maxx',
    // icon: Heart,
    bgColor: '#f3e5f5',
    textColor: '#8e24aa',
  },
  {
    label: 'Maxx-Activated',
    value: 'maxx_activated',
    // icon: Zap,
    bgColor: '#e8f5e9',
    textColor: '#2e7d32',
  },
];

const itemsToHide = [
  'Maxx-Activated',
  'Maxx Portal',
  'Maxx Portal - Activated',
  'Moneeflo Portal - Activated'
]

export const DigitalInterestDropdown: React.FC<CustomDropdownProps> = ({
  items = interestItems,
  onSubmit,
  placeholder = 'Digital Interest',
  selectedValue,
  className,
  triggerClassName,
  disabled = false,
  loading = false,
  hiddenItems = []
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  // Find the selected item based on the label passed in selectedValue
  const selectedItem = items.find(item => item.label === selectedValue);

  const handleItemClick = (item: DropdownItem) => {
    onSubmit(item.value, item.label);
    setIsOpen(false);
  };

  const triggerStyle = selectedItem
    ? { backgroundColor: selectedItem.bgColor, color: selectedItem.textColor }
    : {};
  const hiddenItems2 = [...itemsToHide, ...hiddenItems];
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger className='' asChild>
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-between hover:bg-gray-50",
            "border border-gray-200",
            "px-4 md:px-1 py-2 md:py-[3px] h-auto min-h-[40px]",
            "font-medium text-sm md:text-xs", // consistent font
            !selectedItem && "text-gray-700", // placeholder color
            disabled && "opacity-50 cursor-not-allowed",
            triggerClassName
          )}
          style={triggerStyle}
          disabled={disabled || loading}
        >
          <div className="flex items-center gap-2">
            {selectedItem?.icon && (
              <selectedItem.icon className="w-4 h-4" />
            )}
            <span>
              {selectedItem?.label || placeholder}
            </span>
          </div>
          <ChevronDown
            className={cn(
              "w-4 h-4 md:w-3 md:h-3 transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className={cn("w-full min-w-[200px]", className)}
        align="start"
      >
        {items.map((item) => {
          if(hiddenItems2.includes(item.label)) {
            return null;
          }
          return (
            <DropdownMenuItem
              key={item.value}
              onClick={() => handleItemClick(item)}
              className={cn(
                "flex items-center gap-2 px-4 py-3 cursor-pointer",
                "hover:bg-gray-50 focus:bg-gray-50"
                // Removed Tailwind color classes as inline style is more specific
              )}
              style={{
                backgroundColor: item.bgColor,
                color: item.textColor,
              }}
            >
              {item.icon && (
                <item.icon className="w-4 h-4" />
              )}
              <span className="text-sm font-medium">{item.label === 'Credentials Shared' ? 'Share Credentials' : item.label}</span>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};